import { LocationStrategy } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    Directive,
    Inject,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import {
    ActivatedRoute,
    NavigationEnd,
    Params,
    Router,
    UrlTree,
} from '@angular/router';
import { of, Subscription } from 'rxjs';
import { catchError, filter, map, startWith, tap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../app.config';
import {
    IFrameDirective,
    IFrameService,
    IFrameSubscriber,
} from '../common/iframe.directive';

@Directive()
export class WebFormBase implements AfterViewInit, OnDestroy {
    @ViewChild('iframe', { static: true }) iframe: IFrameDirective;

    ready: boolean;
    protected _sub: Subscription;

    constructor(
        @Inject(CMPS_APP_CONFIG) protected _appConfig: AppConfig,
        protected _iFrameService: IFrameService,
    ) {}

    ngAfterViewInit(): void {
        this._sub = this._iFrameService.messages.subscribe(
            new IFrameSubscriber<any>(this.iframe.id, (message) => {
                if (message.event === 'DOMContentLoaded') {
                    this._onDomContentLoaded(message);
                }
                if (message.event === 'unload') {
                    this._onUnload(message);
                }
                if (message.event === 'click') {
                    this._onClick(message);
                }
                if (message.responseTo === 'getClientHeight') {
                    this._responseToGetClientHeight(message);
                }
            }),
        );
    }

    ngOnDestroy(): void {
        if (this._sub) {
            this._sub.unsubscribe();
        }
    }

    protected _onDomContentLoaded(message: any): void {
        this.ready = true;
    }

    protected _onUnload(message: any): void {
        this.ready = false;
    }

    protected _onClick(message: any): void {
        //
    }

    protected _responseToGetClientHeight(message: any): void {
        //
    }
}

@Component({
    selector: 'cmps-webform',
    template: ` <cmps-wait-status
            [absolute]="true"
            *ngIf="!ready"
        ></cmps-wait-status>
        <iframe
            class="cmps-iframe"
            cmps-iframe
            [newSrc]="currentSrc"
            [hasComm]="true"
            (onload)="ready = true"
            (unload)="ready = false"
            #iframe="cmpsIframe"
        ></iframe>`,
})
export class WebFormComponent extends WebFormBase implements OnInit {
    currentSrc: string;
    private _currentSrcNoBind: string;
    private _arSub: Subscription;
    private _navInvokedNavigation: boolean;
    private readonly _webFormsBaseUrlRegex: RegExp;

    constructor(
        private readonly _activatedRoute: ActivatedRoute,
        private _router: Router,
        private readonly _changeDetectorRef: ChangeDetectorRef,
        private readonly _locationStrategy: LocationStrategy,
        @Inject(CMPS_APP_CONFIG) protected _appConfig: AppConfig,
        protected _iFrameService: IFrameService,
    ) {
        super(_appConfig, _iFrameService);

        // maybe CmpsUrlSerializer should also handle this
        this._webFormsBaseUrlRegex = new RegExp(
            `\\s*${this._appConfig.WEB_FORMS_BASE_URL}`,
            'i',
        );
    }

    ngOnInit(): void {
        this._router.onSameUrlNavigation = 'reload';

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const nextUrl = () =>
            map((queryParams: Params) => {
                // eslint-disable-next-line no-prototype-builtins
                if (queryParams.hasOwnProperty('relativeUrl')) {
                    return `${
                        this._appConfig.WEB_FORMS_BASE_URL
                    }${decodeURIComponent(queryParams.relativeUrl)}`;
                }
                throw new Error('404');
            });

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const skipUrl = () => filter((url) => this._currentSrcNoBind !== url);

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const applyUrl = () =>
            tap((url: string) => {
                this.currentSrc = null;
                this._changeDetectorRef.detectChanges();
                this.currentSrc = this._currentSrcNoBind = url;
                this._changeDetectorRef.detectChanges();
            });

        this._arSub = this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                startWith(this._activatedRoute.snapshot.url),
                map(() => this._activatedRoute.snapshot.queryParams),
                nextUrl(),
                skipUrl(),
                // eslint-disable-next-line no-return-assign
                tap(() => (this._navInvokedNavigation = true)),
                applyUrl(),
                catchError(() => {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    this._router.navigate(['404'], {
                        skipLocationChange: true,
                    });
                    return of(null);
                }),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        if (this._arSub) {
            this._arSub.unsubscribe();
        }
        this._router.onSameUrlNavigation = 'ignore';
        super.ngOnDestroy();
    }

    protected _onDomContentLoaded(message: any): void {
        super._onDomContentLoaded(message);

        if (!this._navInvokedNavigation) {
            this._currentSrcNoBind = message.href;
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._router.navigateByUrl(this._createUrlTree(message.href), {
                replaceUrl: true,
            });
            return;
        }

        if (this._navInvokedNavigation) {
            this._navInvokedNavigation = false;
            // eslint-disable-next-line no-useless-return
            return;
        }
    }

    protected _onClick(message: any): void {
        const e = document.createEvent('MouseEvent');
        e.initEvent('click', true, true);
        document.dispatchEvent(e);
    }

    private _createUrlTree(url: string): UrlTree {
        return this._router.createUrlTree(['internal', 'webform'], {
            queryParams: {
                relativeUrl: url.replace(this._webFormsBaseUrlRegex, ''),
            },
        });
    }
}
